import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from 'antd';
import Card from "components/card";
import { getEmployeeLeaveCreditList } from "../../../../redux/employeeleavecredit/employeeLeaveCreditState";
import { getLeaveCreditForecastList } from "../../../../redux/leavecreditforecast/leaveCreditForecastState";
import { handleTableChange } from "../../../../utils/index";
import dayjs from "dayjs"
import SummaryHeader from "components/header/SummaryHeader";
import SummaryItem from "components/header/SummaryItem";
import {
	ROLE_EMPLOYEE,
} from "variables/constants";
import { 
	getEmployeeList, 
} from "../../../../redux/optionlist/optionlistState";

const EmployeeLeaveCreditsTable = ({ }) => {

	const dispatch = useDispatch();

	const recordList = useSelector((state) => state.employeeLeaveCredit.employeeLeaveCreditList?.items);
	const pagination = useSelector((state) => state.employeeLeaveCredit.employeeLeaveCreditList?.pagination);
	const filters = useSelector((state) => state.employeeLeaveCredit.employeeLeaveCreditList?.filters);
	const authUser = useSelector((state) => state.auth.user);
	const forecastList = useSelector((state) => state.leaveCreditForecast.leaveCreditForecastList?.items);
	const employeeList = useSelector((state) => state.optionlist.employeeList?.items);

	const doSearch = (filters) => dispatch(getEmployeeLeaveCreditList(filters));

	useEffect(() => {
		dispatch(getEmployeeList({}));
	}, [dispatch]);

	const mappedRecordList = (recordList || []).map((record) => ({
		key: record.elc_id,
		date: record.elc_date,
		description: record.elc_date_description,
		vl: record.elc_leave_type_id === 29 ? record : null,
		sl: record.elc_leave_type_id === 30 ? record : null,
	}));

	const forecastVL = forecastList?.find(
		(item) => item.lcf_leave_type_id === 29
	);

	const forecastSL = forecastList?.find(
		(item) => item.lcf_leave_type_id === 30
	);
	const summaryItems = [
		{
			title: "Vacation Leave",
			total: forecastVL?.lcf_current_balance ?? 0,
			icon: "Plane",
			detail: "remaining"
		},
		{
			title: "Sick Leave",
			total: forecastSL?.lcf_current_balance ?? 0,
			icon: "ScanHeart",
			detail: "remaining"
		},
		{
			title: "Forecasted VL",
			total: forecastVL?.lcf_forecast_balance ?? 0,
			icon: "CalendarCheck2",
			detail: "next month"
		},
		{
			title: "Forecasted SL",
			total: forecastSL?.lcf_forecast_balance ?? 0,
			icon: "CalendarHeart",
			detail: "next month"
		},
	];

	const columns = [
		...(recordList?.[0]?.elc_user_id !== authUser?.id
			? [{
				title: `Employee Name`,
				key: 'elc_user_id',
				width: '20%',
				filterMultiple: false,
				filters: employeeList?.map((employee) => ({
						text: employee.label,
						value: employee.value
				})) || [],
				render: (_, record) => (
					<span>
						{record.vl?.elc_employee_name || record.sl?.elc_employee_name}
					</span>
				),
			}]
			: []
		),
		{
			title: "Description",
			key: "description",
			width: recordList?.[0]?.elc_user_id !== authUser?.id ? "20%" : "40%",
			render:(_, record) => (
				<div>
					<div className="font-medium">
						{record.vl?.elc_description || record.sl?.elc_description}
					</div>
					<div className="text-xs muted-text">
						{dayjs(record.date).format("MMMM D, YYYY")}
					</div>
				</div>
			),
		},
		{
			title: "VL Credit",
			key: "vl_credit",
			width: "10%",
			render: (_, record) => record.vl?.elc_credit,
		},
		{
			title: "VL Debit",
			key: "vl_debit",
			width: "10%",
			render: (_, record) => record.vl?.elc_debit,
		},
		{
			title: "VL Balance",
			key: "vl_balance",
			width: "10%",
			render: (_, record) => (
				<div className="font-medium">
					{record.vl?.elc_balance}
				</div>
			),
		},
		{
			title: "SL Credit",
			key: "sl_credit",
			width: "10%",
			render: (_, record) => record.sl?.elc_credit,
		},
		{
			title: "SL Debit",
			key: "sl_debit",
			width: "10%",
			render: (_, record) => record.sl?.elc_debit,
		},
		{
			title: "SL Balance",
			key: "sl_balance",
			width: "10%",
			render: (_, record) => (
				<div className="font-medium">
					{record.sl?.elc_balance}
				</div>
			),
		},
    ];

	useEffect(() => {
		dispatch(getEmployeeLeaveCreditList({
			paginate: pagination.per_page
		}));
	}, [dispatch, pagination.per_page]);

	useEffect(() => {
		dispatch(getLeaveCreditForecastList({
			paginate: 10
		}));
	}, [dispatch]);

  	return (
		<div className="space-y-6">	    
			<SummaryHeader
				title="Leave Credits"
				iconName="ChartCandlestick"
				description="View your used or remaining leave balance"
				color="blue"
			>
				{authUser?.user_level === ROLE_EMPLOYEE && (
					<div className="summary-item-container">
						{summaryItems.map((item) => (
							<SummaryItem
								key={item.title}
								title={item.title}
								iconName={item.icon}
								display={
									<div className="flex items-center gap-2">
										<p className="text-xl font-bold">
												{Number(item.total).toLocaleString(undefined, {
													minimumFractionDigits: 0,
													maximumFractionDigits: 2,
												})} 
										</p>
										<p className="text-xs">
											{item?.detail}
										</p>
									</div>
								}
							/>
						))}
					</div>
				)}
			</SummaryHeader>

			<Card extra={"w-full h-full p-4"}>
				<div className="h-full w-full overflow-x-auto">
					<Table
						dataSource={mappedRecordList} 
						columns={columns} 
						rowKey={(record) => record.key}
						pagination={pagination}    
						scroll={{ x: 'max-content' }}
						onChange={handleTableChange(doSearch, filters)}               	
					/>
				</div>
			</Card>
		</div>

  	);
};

export default EmployeeLeaveCreditsTable;
